import { Column } from 'components/common/table/paginated-table';
import api from 'core/api';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { generateCSVFileDownload } from 'core/utils';
import { pick } from 'lodash';
import { buildURI } from 'react-csv/lib/core';
import { FormResult } from '../types';
import { ReportExport } from './types';

export const handleFormResultDeletion = (
  selectedFormResult: FormResult,
  selectedRecords: any[],
  updateFormResult: (options: any) => void,
  setSelectedRecords: (records: any[]) => void,
  setSelectedFormResult: (result: FormResult) => void,
  onClose: () => void,
  refetch: () => void
) => {
  try {
    const formResults = selectedFormResult
      ? [selectedFormResult]
      : selectedRecords;
    const filteredResults = formResults.filter(
      (x) => x.status === 'In Cart' || x.status === 'In Progress'
    );

    filteredResults.forEach(async (formResult: FormResult) => {
      const variables = {
        id: formResult.formResultId,
        status: 'deleted',
      };

      await updateFormResult({ variables });
    });

    setSelectedRecords([]);
    setSelectedFormResult(null);
    onClose();

    reactiveFeedbackMessage({
      message: `${
        formResults.length > 1 ? 'Registrations have' : 'Registration has'
      } been deleted`,
      isError: false,
      autoclose: true,
    });

    // A timeout is needed to allow Org-Reporting-Service to update the data and build the form.
    setTimeout(() => {
      refetch();
    }, 1500);
  } catch (error) {
    reactiveFeedbackMessage({
      message:
        'Oops! Failed to delete registration(s). Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
};

export const handlePostRegEditSubmit = async (
  data: any,
  selectedFormResult: FormResult,
  setSelectedFormResult: (x: FormResult) => void,
  refetch: () => void
) => {
  try {
    await api.patch(
      `/form_results/${selectedFormResult.formResultId}/update`,
      data
    );

    reactiveFeedbackMessage({
      message: 'Registration has been updated',
      isError: false,
      autoclose: true,
    });

    setSelectedFormResult(null);

    // A timeout is needed to allow Org-Reporting-Service to update the data and build the form.
    setTimeout(() => {
      refetch();
    }, 1500);
  } catch (error) {
    console.log('Error: ', error);

    reactiveFeedbackMessage({
      message:
        'Oops! Failed to update registration. Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
};

export const exportDataToCSV = async (
  columnsToExport: Column[],
  exportType: string,
  orgId: string,
  formId: string,
  exportParams: object,
  onClose: () => void
) => {
  try {
    const response = await api.post<ReportExport>(
      `/organizations/${orgId}/export/form_result_reports/${formId}`,
      {
        ...exportParams,
        exportType,
        columns:
          exportType === 'waivers' ? [] : columnsToExport.map((y) => y.name),
      }
    );
    generateCSVFileDownload(response.data);
    onClose();
  } catch (error) {
    reactiveFeedbackMessage({
      message:
        'Oops! Export could not be created. Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
};

export const exportSelectedRowsToCSV = (
  columnsToExport: Column[],
  selectedRecords: any[],
  onClose: () => void
) => {
  try {
    const headers = columnsToExport.map((x) => ({
      label: x.label,
      key: x.name,
    }));

    const columnNames = columnsToExport.map((y) => y.name);
    const data = selectedRecords.map((x) => pick(x, columnNames));

    window.open(buildURI(data, true, headers, ',', '"'), '_blank');
    onClose();
  } catch (error) {
    reactiveFeedbackMessage({
      message:
        'Oops! Export could not be created. Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
};

type EmailArgs = {
  subjectEmail: string;
  messageEmail: string;
  sendFromEmail: string;
};

export const sendEmailRequest = async (
  emailArgs: EmailArgs,
  isSendingEmailToAllRegistrants: boolean,
  isSendingIndividualEmail: boolean,
  selectedRecords: any[],
  selectedFormResult: FormResult,
  orgId: string,
  formId: string,
  searchTerm: string,
  formatFilters: (filters: object) => object,
  activeFilters: object,
  resetEmailStates: () => void
) => {
  try {
    const { subjectEmail, messageEmail, sendFromEmail } = emailArgs;
    const data = {
      subject: subjectEmail,
      message: messageEmail,
      fromEmail: sendFromEmail,
      bcc_form_result_ids:
        isSendingEmailToAllRegistrants || isSendingIndividualEmail
          ? null
          : selectedRecords?.map((r) => r.formResultId),
      to_form_result_id:
        isSendingIndividualEmail && selectedFormResult
          ? selectedFormResult.formResultId
          : null,
      searchTerm,
      filters: formatFilters(activeFilters),
    };
    const response = await api.post(
      `/organizations/${orgId}/send_email_to_registrants/${formId}`,
      data
    );
    if (response?.data === 'ok') {
      reactiveFeedbackMessage({
        message: 'Your email has been sent',
        isError: false,
        autoclose: true,
      });
      resetEmailStates();
      return true;
    }
  } catch (e) {
    reactiveFeedbackMessage({
      message:
        'Oops! Failed to send email. Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
  return false;
};
